// Signage → Combos de temporada (/signage/combos). Kits/packs curados (productos
// reales del catálogo + descuento del pack, vigentes por rango de fecha) que la
// escena `combo` del reproductor muestra por sucursal. CRUD + editor de ítems con
// buscador de productos y preview del precio del pack.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Package, Pencil, Plus, Search, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Campo, Modal, Switch, fieldCls } from '@/features/pos/shared';
import { SIG, sigInsert, sigList, sigSoftDelete, sigUpdate } from './shared';

interface Combo {
  id: string; codigo: string | null; nombre: string; etiqueta: string | null; descripcion: string | null;
  descuento_pct: number; fecha_inicio: string | null; fecha_fin: string | null; orden: number; es_activo: boolean;
}
interface ItemForm { producto_id: string; nombre: string; precio: number; cantidad: number }
interface ProdRow { id: string; codigo: string; nombre: string; precio_base: number }

const money = (n: number) => `$${(Number.isFinite(n) ? n : 0).toFixed(2)}`;
const vacio = { codigo: '', nombre: '', etiqueta: '', descripcion: '', descuento_pct: 10, fecha_inicio: '', fecha_fin: '', orden: 0, es_activo: true };

export function Combos() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: combos = [], isFetching } = useQuery<Combo[]>({
    queryKey: ['sig-combos'], queryFn: () => sigList<Combo>('combo?select=*&deleted_at=is.null&order=orden,nombre'),
  });

  const [abierto, setAbierto] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState(vacio);
  const [items, setItems] = useState<ItemForm[]>([]);
  const [busq, setBusq] = useState('');

  const { data: resultados = [] } = useQuery<ProdRow[]>({
    queryKey: ['sig-combo-prodsearch', busq],
    enabled: abierto && busq.trim().length >= 2,
    queryFn: async () => (await apiClient.get<ProdRow[]>(
      `/rest/inventario.productos?select=id,codigo,nombre,precio_base&es_activo=eq.true&deleted_at=is.null&precio_base=gt.0&nombre=ilike.*${encodeURIComponent(busq.trim())}*&order=nombre&limit=20`)).data,
    staleTime: 30_000,
  });

  const suma = useMemo(() => items.reduce((a, it) => a + it.precio * it.cantidad, 0), [items]);
  const pack = useMemo(() => Math.round(suma * (1 - (Number(form.descuento_pct) || 0) / 100) * 100) / 100, [suma, form.descuento_pct]);

  const abrir = async (c?: Combo) => {
    setBusq('');
    if (c) {
      setEditId(c.id);
      setForm({ codigo: c.codigo ?? '', nombre: c.nombre, etiqueta: c.etiqueta ?? '', descripcion: c.descripcion ?? '',
        descuento_pct: c.descuento_pct, fecha_inicio: c.fecha_inicio ?? '', fecha_fin: c.fecha_fin ?? '', orden: c.orden, es_activo: c.es_activo });
      const its = await sigList<{ producto_id: string; cantidad: number }>(`combo_item?select=producto_id,cantidad&combo_id=eq.${c.id}&deleted_at=is.null&order=orden`);
      if (its.length) {
        const ids = its.map((i) => i.producto_id).join(',');
        const prods = (await apiClient.get<ProdRow[]>(`/rest/inventario.productos?select=id,codigo,nombre,precio_base&id=in.(${ids})`)).data;
        const byId = Object.fromEntries(prods.map((p) => [p.id, p]));
        setItems(its.map((i) => ({ producto_id: i.producto_id, cantidad: Number(i.cantidad), nombre: byId[i.producto_id]?.nombre ?? '—', precio: Number(byId[i.producto_id]?.precio_base ?? 0) })));
      } else setItems([]);
    } else { setEditId(null); setForm(vacio); setItems([]); }
    setAbierto(true);
  };

  const agregar = (p: ProdRow) => {
    if (items.some((it) => it.producto_id === p.id)) return;
    setItems((xs) => [...xs, { producto_id: p.id, nombre: p.nombre, precio: Number(p.precio_base), cantidad: 1 }]);
    setBusq('');
  };

  const guardar = useMutation({
    mutationFn: async () => {
      if (!form.nombre.trim()) throw new Error('El nombre es obligatorio');
      if (!items.length) throw new Error('Agrega al menos un producto al combo');
      const body = {
        codigo: form.codigo || null, nombre: form.nombre.trim(), etiqueta: form.etiqueta || null,
        descripcion: form.descripcion || null, descuento_pct: Number(form.descuento_pct) || 0,
        fecha_inicio: form.fecha_inicio || null, fecha_fin: form.fecha_fin || null, orden: Number(form.orden) || 0, es_activo: form.es_activo,
      };
      let id = editId;
      if (editId) await sigUpdate('combo', editId, body);
      else id = (await sigInsert<{ id: string }>('combo', body)).id;
      await apiClient.delete(`/rest/signage.combo_item?combo_id=eq.${id}`, SIG);
      for (let i = 0; i < items.length; i++) {
        await sigInsert('combo_item', { combo_id: id, producto_id: items[i].producto_id, cantidad: items[i].cantidad, orden: i });
      }
    },
    onSuccess: () => { toast.success('Combo guardado'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['sig-combos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => sigSoftDelete('combo', id),
    onSuccess: () => { toast.success('Combo eliminado'); void qc.invalidateQueries({ queryKey: ['sig-combos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4 max-w-5xl">
      {dlg.ui}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2"><Package className="w-5 h-5" /> Combos de temporada</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Kits/packs curados que aparecen en las pantallas con una escena tipo "Combo". Productos reales del catálogo + descuento del pack. <strong>Coherencia pantalla↔caja:</strong> al guardar, el combo crea/actualiza automáticamente su promoción en el POS — la pantalla muestra el precio que la caja cobra, y un combo con algún producto agotado en el local no se exhibe.</p>
        </div>
        <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Nuevo combo</Button>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Combos ({combos.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Nombre</th><th className="py-2 pr-3">Etiqueta</th><th className="py-2 pr-3 text-right">Desc.</th>
              <th className="py-2 pr-3">Vigencia</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {combos.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Sin combos. Crea el primero (p. ej. "Kit Antigripe").</td></tr>}
              {combos.map((c) => (
                <tr key={c.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-medium">{c.nombre}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{c.etiqueta || '—'}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{Number(c.descuento_pct)}%</td>
                  <td className="py-2 pr-3 text-muted-foreground text-xs">{c.fecha_inicio || 'siempre'} → {c.fecha_fin || '∞'}</td>
                  <td className="py-2 pr-3">{c.es_activo ? 'Activo' : 'Inactivo'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => abrir(c)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title="Eliminar"
                      onClick={async () => { if (await dlg.confirmar(`¿Eliminar "${c.nombre}"?`, { peligro: true })) eliminar.mutate(c.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {isFetching && <p className="text-xs text-muted-foreground mt-2">Cargando…</p>}
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editId ? 'Editar combo' : 'Nuevo combo'} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Nombre del kit *"><input className={fieldCls} value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Kit Antigripe" /></Campo>
              <Campo label="Etiqueta / temporada"><input className={fieldCls} value={form.etiqueta} onChange={(e) => setForm({ ...form, etiqueta: e.target.value })} placeholder="Temporada de gripe" /></Campo>
            </div>
            <Campo label="Descripción"><input className={fieldCls} value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} placeholder="Todo lo que necesitas para la gripe" /></Campo>
            <div className="grid grid-cols-4 gap-3">
              <Campo label="Descuento %"><input type="number" min="0" max="90" className={fieldCls} value={form.descuento_pct} onChange={(e) => setForm({ ...form, descuento_pct: Number(e.target.value) })} /></Campo>
              <Campo label="Desde"><input type="date" className={fieldCls} value={form.fecha_inicio} onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })} /></Campo>
              <Campo label="Hasta"><input type="date" className={fieldCls} value={form.fecha_fin} onChange={(e) => setForm({ ...form, fecha_fin: e.target.value })} /></Campo>
              <Campo label="Orden"><input type="number" className={fieldCls} value={form.orden} onChange={(e) => setForm({ ...form, orden: Number(e.target.value) })} /></Campo>
            </div>

            {/* Productos del combo */}
            <div className="border rounded-md p-3 space-y-2">
              <div className="text-sm font-medium">Productos del pack</div>
              {items.map((it, i) => (
                <div key={it.producto_id} className="flex items-center gap-2 text-sm">
                  <span className="flex-1 truncate">{it.nombre}</span>
                  <span className="text-muted-foreground tabular-nums">{money(it.precio)}</span>
                  <input type="number" min="1" className={`${fieldCls} !h-8 !w-16`} value={it.cantidad}
                    onChange={(e) => setItems((xs) => xs.map((x, k) => (k === i ? { ...x, cantidad: Math.max(1, Number(e.target.value) || 1) } : x)))} />
                  <button className="text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded p-1" onClick={() => setItems((xs) => xs.filter((_, k) => k !== i))}><X className="w-4 h-4" /></button>
                </div>
              ))}
              {!items.length && <div className="text-xs text-muted-foreground">Busca y agrega productos abajo.</div>}
              <div className="relative">
                <div className="flex items-center gap-2 border rounded-md px-2">
                  <Search className="w-4 h-4 text-muted-foreground" />
                  <input className="flex-1 bg-transparent py-1.5 text-sm outline-none" placeholder="Buscar producto por nombre…" value={busq} onChange={(e) => setBusq(e.target.value)} />
                </div>
                {busq.trim().length >= 2 && resultados.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full max-h-52 overflow-y-auto border rounded-md bg-popover shadow-lg">
                    {resultados.map((p) => (
                      <button key={p.id} className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted flex justify-between gap-2" onClick={() => agregar(p)}>
                        <span className="truncate">{p.nombre}</span><span className="text-muted-foreground tabular-nums shrink-0">{money(Number(p.precio_base))}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Preview del precio del pack */}
            {items.length > 0 && (
              <div className="flex items-center justify-between rounded-md bg-muted/40 px-3 py-2 text-sm">
                <span className="text-muted-foreground">Por separado <span className="line-through">{money(suma)}</span></span>
                <span className="font-semibold">Pack {money(pack)} <span className="text-emerald-600">· ahorra {money(suma - pack)}</span></span>
              </div>
            )}

            <Switch checked={form.es_activo} onChange={(v) => setForm({ ...form, es_activo: v })} label="Activo" />
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.nombre.trim() || !items.length} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
